import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeftIcon, 
  PencilIcon, 
  DocumentTextIcon, 
  ClockIcon, 
  PlusIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  CalendarDaysIcon,
  InformationCircleIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import api from '../../utils/api';
import CaseResearchWidget from '../../components/research/CaseResearchWidget';

const statusConfig = {
  open:     { label: 'Open',     bg: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',     dot: 'bg-blue-500',   step: 0 },
  active:   { label: 'Active',   bg: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200', dot: 'bg-green-500',  step: 1 },
  pending:  { label: 'Pending',  bg: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200', dot: 'bg-yellow-500', step: 2 },
  closed:   { label: 'Closed',   bg: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',     dot: 'bg-gray-500',   step: 3 },
  archived: { label: 'Archived', bg: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200', dot: 'bg-purple-500', step: 4 },
};

const STATUS_STEPS = ['open', 'active', 'pending', 'closed'];

/** Status Progress Stepper */
const StatusStepper = ({ currentStatus }) => {
  const currentStep = statusConfig[currentStatus?.toLowerCase()]?.step ?? 0;
  return (
    <div className="flex items-center gap-0" data-testid="status-stepper" aria-label="Case status progress">
      {STATUS_STEPS.map((step, idx) => {
        const cfg = statusConfig[step];
        const isCompleted = idx < currentStep;
        const isCurrent = step === currentStatus?.toLowerCase();
        return (
          <React.Fragment key={step}>
            <div className="flex flex-col items-center">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-colors ${
                  isCompleted
                    ? 'bg-primary-600 border-primary-600 text-white'
                    : isCurrent
                    ? `${cfg.dot} border-current text-white`
                    : 'bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-400'
                }`}
              >
                {isCompleted ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  idx + 1
                )}
              </div>
              <span className={`text-[10px] mt-1 font-medium whitespace-nowrap ${isCurrent ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500'}`}>
                {cfg.label}
              </span>
            </div>
            {idx < STATUS_STEPS.length - 1 && (
              <div className={`flex-1 h-0.5 mb-4 mx-1 ${idx < currentStep ? 'bg-primary-500' : 'bg-gray-200 dark:bg-gray-700'}`} style={{ minWidth: '2rem' }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

const CaseDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const fetchCaseDetails = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/api/cases/${id}/`);
        const data = response?.data ?? response;
        setCaseData(data?.results ? data.results : data);
        setError(null);
      } catch (err) {
        setError(err.response?.data?.detail || err.message || `Failed to fetch case details for ID ${id}.`);
        console.error(`Failed to fetch case details for ID ${id}:`, err);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchCaseDetails();
    }
  }, [id]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/api/cases/${id}/`);
      navigate('/cases', { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete case. Please try again.');
      setShowDeleteModal(false);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading case details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-10">
        <ExclamationTriangleIcon className="h-12 w-12 text-red-400 mx-auto mb-3" />
        <p className="text-red-500 text-lg font-medium">Error loading case</p>
        <p className="text-gray-600 dark:text-gray-400 mt-1">{error}</p>
        <button
          onClick={() => navigate('/cases')}
          className="mt-4 inline-flex items-center px-4 py-2 text-sm font-medium text-primary-600 border border-primary-300 rounded-lg hover:bg-primary-50 dark:text-primary-400 dark:border-primary-600 dark:hover:bg-primary-900/20"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-2" /> Back to Cases
        </button>
      </div>
    );
  }

  if (!caseData) {
    return <div className="text-center py-10 text-gray-600 dark:text-gray-400">No case data found.</div>;
  }
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  };

  const statusCfg = statusConfig[caseData.status?.toLowerCase()] || statusConfig.open;

  return (
    <>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/cases"
            className="text-sm text-gray-500 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 flex items-center mb-3 transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" /> Back to Cases
          </Link>

          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{caseData.title}</h2>
                <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full ${statusCfg.bg}`}>
                  {statusCfg.label}
                </span>
                {caseData.case_type && (
                  <span className="px-2.5 py-0.5 text-xs font-medium rounded-full bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200">
                    {caseData.case_type}
                  </span>
                )}
              </div>
              {caseData.case_number && (
                <p className="text-sm text-gray-500 dark:text-gray-400 font-mono">Case No: {caseData.case_number}</p>
              )}
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              <Link
                to={`/cases/${caseData.id}/edit`}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg shadow-sm hover:bg-primary-700 transition-colors"
              >
                <PencilIcon className="h-4 w-4 mr-2" /> Edit
              </Link>
              <button
                onClick={() => setShowDeleteModal(true)}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-red-600 dark:text-red-400 bg-white dark:bg-gray-800 border border-red-300 dark:border-red-600 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              >
                <TrashIcon className="h-4 w-4 mr-2" /> Delete
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Core Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Case Information Card */}
            <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Case Information</h3>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-sm">
                <div>
                  <dt className="font-medium text-gray-500 dark:text-gray-400 mb-0.5">Client</dt>
                  <dd className="text-gray-900 dark:text-white">
                    {caseData.client_details ? (
                      <Link to={`/clients/${caseData.client_details.id}`} className="text-primary-600 dark:text-primary-400 hover:underline font-medium">
                        {caseData.client_details.first_name} {caseData.client_details.last_name}
                      </Link>
                    ) : (caseData.client || 'N/A')}
                  </dd>
                </div>
                <div>
                  <dt className="font-medium text-gray-500 dark:text-gray-400 mb-0.5">Case Type</dt>
                  <dd>
                    {caseData.case_type ? (
                      <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200">
                        {caseData.case_type}
                      </span>
                    ) : <span className="text-gray-400">N/A</span>}
                  </dd>
                </div>
                <div>
                  <dt className="font-medium text-gray-500 dark:text-gray-400 mb-0.5">Date Opened</dt>
                  <dd className="text-gray-900 dark:text-white">{formatDate(caseData.date_opened)}</dd>
                </div>
                <div>
                  <dt className="font-medium text-gray-500 dark:text-gray-400 mb-0.5">Date Closed</dt>
                  <dd className="text-gray-900 dark:text-white">{caseData.date_closed ? formatDate(caseData.date_closed) : <span className="text-gray-400">Still open</span>}</dd>
                </div>
                <div>
                  <dt className="font-medium text-gray-500 dark:text-gray-400 mb-0.5">Last Updated</dt>
                  <dd className="text-gray-900 dark:text-white">{formatDate(caseData.last_updated)}</dd>
                </div>
                {caseData.case_number && (
                  <div>
                    <dt className="font-medium text-gray-500 dark:text-gray-400 mb-0.5">Case Number</dt>
                    <dd className="text-gray-900 dark:text-white font-mono text-xs">{caseData.case_number}</dd>
                  </div>
                )}
                <div className="sm:col-span-2">
                  <dt className="font-medium text-gray-500 dark:text-gray-400 mb-1">Assigned Team</dt>
                  <dd>
                    {caseData.assigned_attorneys_details && caseData.assigned_attorneys_details.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {caseData.assigned_attorneys_details.map((attorney) => (
                          <span key={attorney.id} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-xs font-medium text-gray-700 dark:text-gray-300">
                            <span className="w-5 h-5 rounded-full bg-primary-500 text-white flex items-center justify-center text-[10px] font-bold" aria-hidden="true">
                              {attorney.first_name?.[0]}{attorney.last_name?.[0]}
                            </span>
                            {attorney.first_name} {attorney.last_name}
                            {attorney.role && (
                              <span className="text-gray-400 dark:text-gray-500 capitalize">· {attorney.role}</span>
                            )}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500">No team members assigned</span>
                    )}
                  </dd>
                </div>
              </dl>
            </div>

            {/* Description Card */}
            <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <InformationCircleIcon className="h-5 w-5 text-gray-400" /> Description
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                {caseData.description || <span className="text-gray-400 italic">No description provided.</span>}
              </p>
            </div>

            {/* Activity Timeline */}
            <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-200 dark:border-gray-700 p-6" data-testid="activity-timeline">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <ClockIcon className="h-5 w-5 text-gray-400" /> Activity Timeline
              </h3>
              <ol className="relative border-l-2 border-gray-200 dark:border-gray-700 pl-4 space-y-4">
                {caseData.last_updated && (
                  <li className="relative">
                    <span className="absolute -left-[1.3rem] top-1 w-4 h-4 rounded-full border-2 border-primary-500 bg-white dark:bg-gray-800 flex items-center justify-center">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary-500 block" />
                    </span>
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200">Case last updated</p>
                    <time className="text-xs text-gray-500 dark:text-gray-400">{formatDate(caseData.last_updated)}</time>
                  </li>
                )}
                {caseData.date_closed && (
                  <li className="relative">
                    <span className="absolute -left-[1.3rem] top-1 w-4 h-4 rounded-full border-2 border-gray-400 bg-white dark:bg-gray-800 flex items-center justify-center">
                      <span className="w-1.5 h-1.5 rounded-full bg-gray-400 block" />
                    </span>
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200">Case closed</p>
                    <time className="text-xs text-gray-500 dark:text-gray-400">{formatDate(caseData.date_closed)}</time>
                  </li>
                )}
                {caseData.date_opened && (
                  <li className="relative">
                    <span className="absolute -left-[1.3rem] top-1 w-4 h-4 rounded-full border-2 border-green-500 bg-white dark:bg-gray-800 flex items-center justify-center">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500 block" />
                    </span>
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200">Case opened</p>
                    <time className="text-xs text-gray-500 dark:text-gray-400">{formatDate(caseData.date_opened)}</time>
                  </li>
                )}
              </ol>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Status Progress */}
            <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-4">Case Progress</h3>
              <StatusStepper currentStatus={caseData.status} />
            </div>

            {/* Key Dates */}
            <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-200 dark:border-gray-700 p-6" data-testid="key-dates-section">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <CalendarDaysIcon className="h-5 w-5 text-gray-400" /> Key Dates
              </h3>
              <dl className="space-y-3 text-sm">
                <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
                  <dt className="text-gray-500 dark:text-gray-400">Opened</dt>
                  <dd className="font-medium text-gray-900 dark:text-white text-right">{formatDate(caseData.date_opened)}</dd>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
                  <dt className="text-gray-500 dark:text-gray-400">Closed</dt>
                  <dd className="font-medium text-gray-900 dark:text-white text-right">
                    {caseData.date_closed ? formatDate(caseData.date_closed) : <span className="text-gray-400 italic text-xs">Ongoing</span>}
                  </dd>
                </div>
                <div className="flex justify-between items-center py-2">
                  <dt className="text-gray-500 dark:text-gray-400">Last Updated</dt>
                  <dd className="font-medium text-gray-900 dark:text-white text-right">{formatDate(caseData.last_updated)}</dd>
                </div>
              </dl>
            </div>

            {/* Documents */}
            <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <DocumentTextIcon className="h-5 w-5 text-gray-400" /> Documents
              </h3>
              {caseData.documents && caseData.documents.length > 0 ? (
                <ul className="space-y-2">
                  {caseData.documents.map((doc) => (
                    <li key={doc.id} className="flex items-center justify-between py-1">
                      <Link to={`/documents/${doc.id}`} className="text-sm text-primary-600 dark:text-primary-400 hover:underline truncate">
                        {doc.name}
                      </Link>
                      <span className="text-xs text-gray-500 dark:text-gray-400 ml-2 flex-shrink-0">
                        {new Date(doc.uploaded_at).toLocaleDateString()}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-2">No documents attached.</p>
              )}
              <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
                <Link
                  to={`/documents/upload?caseId=${caseData.id}`}
                  className="inline-flex items-center text-sm text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 font-medium"
                >
                  <PlusIcon className="h-4 w-4 mr-1" /> Add Document
                </Link>
              </div>
            </div>

            {/* Case Research Widget */}
            <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" /> Legal Research
              </h3>
              <CaseResearchWidget caseId={caseData.id} caseName={caseData.title} />
              <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 text-center">
                <Link
                  to={`/research/case/${caseData.id}`}
                  className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 font-medium"
                >
                  View full research dashboard
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/50" onClick={() => !deleting && setShowDeleteModal(false)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-full">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Delete Case</h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
              Are you sure you want to delete <strong>"{caseData.title}"</strong>? This action cannot be undone and will remove all associated data.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 inline-flex items-center"
              >
                {deleting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Deleting...
                  </>
                ) : (
                  <>
                    <TrashIcon className="h-4 w-4 mr-2" />
                    Delete Case
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CaseDetails;
